import { FC, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks';
import userService from '../services/user';
import { setActivity, STREAK_GOAL_SECONDS } from '../reducers/activityReducer';
import { updateUserStreak } from '../reducers/userReducer';
import { notify } from '../reducers/notificationReducer';
import type { UserState } from '../types/common';

// Only deliberate engagement resets the idle timer (per product spec: scrolling,
// commenting, posting, reacting — all of which are scroll/click/key/touch).
const ACTIVITY_EVENTS = ['scroll', 'click', 'keydown', 'touchstart', 'pointerdown'];
const IDLE_MS = 60_000; // >1 min without activity pauses the active timer
const TICK_MS = 1_000;
const HEARTBEAT_MS = 20_000;
const STORAGE_KEY = 'dailyActivity';

const todayString = () => new Date().toISOString().slice(0, 10);

/**
 * Headless tracker for the daily-streak active-time requirement. Counts a second
 * as "active" only while the tab is visible AND there was engagement in the last
 * minute, persists progress locally, and syncs to the server (which clamps and
 * awards the streak at the goal). Runs only while logged in; no visible UI of
 * its own — the progress it produces is shown by StreakCard.
 */
const ActivityTracker: FC = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user as UserState | null);
  const loggedIn = Boolean(user?.token);

  useEffect(() => {
    if (!loggedIn) return;

    let day = todayString();
    let activeSeconds = 0;
    let earned = false;

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved?.date === day) {
          activeSeconds = Math.min(Number(saved.activeSeconds) || 0, STREAK_GOAL_SECONDS);
          earned = Boolean(saved.earned);
        }
      }
    } catch {
      /* ignore corrupt cache */
    }

    let lastActivity = Date.now();
    let lastSentSeconds = -1;
    let firstSync = true;

    const persist = () => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ date: day, activeSeconds, earned }));
      } catch {
        /* storage full / unavailable — non-fatal */
      }
    };

    const pushState = () =>
      dispatch(
        setActivity({ date: day, activeSeconds, goalSeconds: STREAK_GOAL_SECONDS, earnedToday: earned })
      );

    pushState();

    const markActivity = () => {
      lastActivity = Date.now();
    };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, markActivity, { passive: true }));

    const heartbeat = async () => {
      if (earned) return; // goal already met today — nothing left to sync
      if (activeSeconds === lastSentSeconds) return;
      lastSentSeconds = activeSeconds;
      try {
        const res = await userService.recordActivity(activeSeconds);
        const wasEarned = earned;
        const serverSeconds = Number(res.activeSeconds);
        activeSeconds = Math.min(
          Number.isFinite(serverSeconds) ? serverSeconds : activeSeconds,
          STREAK_GOAL_SECONDS
        );
        earned = Boolean(res.earnedToday);
        persist();
        pushState();
        if (earned && !wasEarned) {
          if (res.streak) dispatch(updateUserStreak(res.streak));
          if (!firstSync) {
            dispatch(notify("Daily streak earned! 🔥 See you tomorrow to keep it going.", 'success'));
          }
        }
      } catch {
        lastSentSeconds = -1; // let the next tick retry
      } finally {
        firstSync = false;
      }
    };

    // Sync with the server's stored value straight away (covers other devices /
    // a mid-day reload) rather than waiting a full heartbeat interval.
    void heartbeat();

    const tick = window.setInterval(() => {
      const now = Date.now();
      const currentDay = todayString();
      if (currentDay !== day) {
        // New UTC day: reset progress.
        day = currentDay;
        activeSeconds = 0;
        earned = false;
        lastSentSeconds = -1;
        persist();
        pushState();
      }
      if (earned) return;
      const active = document.visibilityState === 'visible' && now - lastActivity <= IDLE_MS;
      if (active) {
        activeSeconds = Math.min(activeSeconds + 1, STREAK_GOAL_SECONDS);
        if (activeSeconds % 5 === 0) persist();
        pushState();
      }
    }, TICK_MS);

    const heartbeatTimer = window.setInterval(() => void heartbeat(), HEARTBEAT_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        persist();
        void heartbeat();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(tick);
      window.clearInterval(heartbeatTimer);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, markActivity));
      document.removeEventListener('visibilitychange', onVisibility);
      persist();
    };
  }, [loggedIn, dispatch]);

  return null;
};

export default ActivityTracker;
